import React, { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowBack, Share, AccessTime, Visibility, Article, Campaign } from '@mui/icons-material';
import newsService from '../utils/newsService';
import Loader from '../components/Loader';
import { snackbar } from '../utils/showMessage';

const BASE_FILE = process.env.REACT_APP_FILE_BASE_URL;

const NewsDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [newsItem, setNewsItem] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadNews();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const loadNews = async () => {
        try {
            setLoading(true);
            const data = await newsService.getById(id);
            setNewsItem(data);
        } catch (error) {
            console.error('Error loading news:', error);
            snackbar('خطا در بارگذاری خبر');
            navigate('/news');
        } finally {
            setLoading(false);
        }
    };

    const handleShare = () => {
        if (navigator.share) {
            navigator.share({
                title: newsItem.title,
                text: newsItem.summary || newsItem.content,
                url: window.location.href,
            }).catch(err => console.error('Error sharing:', err));
        } else {
            navigator.clipboard.writeText(window.location.href);
            snackbar('لینک کپی شد');
        }
    };

    const getImageUrl = (img) => {
        if (!img) return '';
        if (img.startsWith('http')) return img;
        return `${BASE_FILE}/uploads/documents/${img}`;
    };

    const formatDate = (date) => {
        return new Date(date).toLocaleDateString('fa-IR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    if (loading) {
        return <Loader />;
    }

    if (!newsItem) {
        return null;
    }

    return (
        <div className="main-container container">
            {/* Header */}
            <div className="row mb-3">

                <div className="col">
                    <div className="d-flex align-items-center">
                        {newsItem.type === 'announcement' ? (
                            <Campaign className="text-warning me-2" />
                        ) : (
                            <Article className="text-primary me-2" />
                        )}
                        <span className="badge bg-light text-dark">
                            {newsItem.type === 'announcement' ? 'اطلاعیه' : 'خبر'}
                        </span>
                    </div>
                </div>


            </div>

            {/* Title */}
            <div className="row mb-3">
                <div className="col-12">
                    <h4 className="mb-0">{newsItem.title}</h4>
                </div>
            </div>

            {/* Meta Info */}
            <div className="row mb-3">
                <div className="col-12">
                    <div className="d-flex justify-content-between text-muted">
                        <small>
                            <AccessTime fontSize="small" className="me-1" />
                            {formatDate(newsItem.publishDate || newsItem.createdAt)}
                        </small>
                        <small>
                            <Visibility fontSize="small" className="me-1" />
                            {newsItem.viewCount || 0} بازدید
                        </small>
                    </div>
                </div>
            </div>

            {/* Image */}
            {newsItem.image && (
                <div className="row mb-3" style={{
                    position: "relative"
                }}>
                    <div className="col-12">
                        <img
                            src={getImageUrl(newsItem.image)}
                            alt={newsItem.title}
                            className="w-100 rounded-15"
                            style={{ objectFit: 'contain' }}
                        />
                        <button
                            style={{
                                position: "absolute",
                                top: 10,
                                left: 15,
                            }}
                            className="btn btn-light btn-44 btn-rounded" onClick={handleShare}>
                            <Share />
                        </button>
                    </div>
                </div>
            )}

            {/* Summary */}
            {newsItem.summary && (
                <div className="row mb-3">
                    <div className="col-12">
                        <div className="alert alert-info">
                            <strong>خلاصه:</strong> {newsItem.summary}
                        </div>
                    </div>
                </div>
            )}

            {/* Content */}
            <div className="row mb-3">
                <div className="col-12">
                    <div className="card">
                        <div className="card-body">
                            <div
                                className="text-justify"
                                style={{ lineHeight: '2', whiteSpace: 'pre-wrap' }}
                                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(newsItem.content || '') }}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Gallery */}
            {newsItem.gallery && newsItem.gallery.length > 0 && (
                <div className="row mb-3">
                    <div className="col-12">
                        <h6 className="mb-3">تصاویر گالری</h6>
                        <div className="row">
                            {newsItem.gallery.map((img, index) => (
                                <div key={index} className="col-6 col-md-4 mb-2">
                                    <img
                                        src={getImageUrl(img)}
                                        alt={`${newsItem.title} - ${index + 1}`}
                                        className="w-100 rounded-12 shadow-sm"
                                        style={{ aspectRatio: '4/3', objectFit: 'cover' }}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Tags */}
            {newsItem.tags && newsItem.tags.length > 0 && (
                <div className="row mb-3">
                    <div className="col-12">
                        <div className="d-flex flex-wrap gap-2">
                            {newsItem.tags.map((tag, index) => (
                                <span key={index} className="badge bg-light text-dark">
                                    #{tag}
                                </span>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default NewsDetail;
